import styled from 'styled-components'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { API } from '../../../../components/API'
import { setPickedCollection } from '../../../../components/store/pickedCollection'

import { ICollection } from '../../../../components/types/collection'
import Skeleton from '../../../../UI/components/UIKit/Skeleton'
import { MAIN_COLORS } from '../../../../UI/consts/colors'
import Ring from './Ring'

interface IProps {
  data:ICollection
}

export const Wrapper = styled.div<{blur:boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: ${MAIN_COLORS.gradient_purple_200};
  border-radius: 12px;
  padding: 12px 8px;
  position: relative;
  cursor: pointer;

  img {
    opacity: ${p => p.blur ? 0.4 : 1};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${p => p.blur? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
    border-radius: 12px;
    pointer-events: none;
    z-index: 3;
  }
`
export const InfoWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;

  h3 {
    color: ${MAIN_COLORS.purple_600};

    span {
      color: ${MAIN_COLORS.yellow};
    }
  }
`
const RingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 35vw;
  height: 35vw;
`;
const InnerImageWrap = styled.div`
  width: 34vw;
  height: 33vw;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  border-radius: 50%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const AllBadge = styled.div<{type: 'all' | 'new' | string}>`
  position: absolute;
  z-index: 2;
  top: 6px;
  right: -4px;
  padding: 4px 6px;
  background: ${p => p.type === 'all' ? MAIN_COLORS.yellow : MAIN_COLORS.pink};
  color: ${p => p.type === 'all' ? MAIN_COLORS.purple_200 : MAIN_COLORS.yellow};

  ${p => p.type !== 'all' && p.type !== 'new' && `
    background: #FF5C33;
    color: #ffffff;
  `}
  border-radius: 4px;
`;

const CollectionItem:FC<IProps> = ({data}) => {
  const navigate = useNavigate();
  const [ image, setImage ] = useState<string>('');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [ imgIsLoading, setImgIsLoading ] = useState(true);
  const [ hasFetchedImage, setHasFetchedImage ] = useState(false);
  const progress = (data.user_collected / data.size) * 100;
  const bageType = data.is_outstock ? data.tag 
    : !data.is_outstock && data.tag ? 'new' 
    : data.user_collected === data.size ? 'all' : null;

  const relocate = () => {
    setPickedCollection(data);
    navigate('/MainPage');
  }
  
  const getImage = async () => {
    const res = await API.getImageData(data.img_id,'collections');
    setImage(res);
  }
  
  useEffect( () => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFetchedImage) {
          getImage().then(() => {
            setImgIsLoading(false);
            setHasFetchedImage(true);
          });
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) observer.observe(cardRef.current);

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [hasFetchedImage])
  
  return (
    <Wrapper ref={cardRef} onClick={relocate} blur={data.is_outstock}>
      <RingWrap>
        <div style={{position:'absolute', zIndex:2}}>
          <Ring progress={progress}/>
        </div>
        <InnerImageWrap>
          {imgIsLoading ? <Skeleton width={'34vw'} height={'33vw'}/> : 
            <img
              src={image}
              alt=''
            />
          }
        </InnerImageWrap>
      </RingWrap>
      <InfoWrap>
        <h4>{data.name}</h4>
        <h3><span>{data.user_collected}</span> / {data.size}</h3>
      </InfoWrap>
      { bageType && 
        <AllBadge type={bageType}>
          <h4>{bageType.toUpperCase()}</h4>
        </AllBadge> 
      }
    </Wrapper>
  )
}

export default memo(CollectionItem)